import styles from '../document-preview/DocumentPreview.module.scss';

import { Theme, useMediaQuery } from '@mui/material';
import React, { useCallback, useMemo, useState } from 'react';

import { usePermissions } from '@work4all/data';

import { ReViewModel } from '@work4all/models/lib/Classes/ReViewModel.entity';
import { Entities } from '@work4all/models/lib/Enums/Entities.enum';
import { convertEntityToBzObjType } from '@work4all/models/lib/utils/convertEntityToBzObjType';

import { LanguageResource } from '@work4all/utils/lib/i18n/LanguageResource.enum';

import { CustomToolbar } from '../../../dataDisplay/actions-bar/ToolbarTypes';
import { MIME_TYPES } from '../../../preview/Preview';
import { IConvertPopoverProps } from '../../convert-popover/ConvertPopover';
import { useEntityTemplate } from '../../entity-template-provider/EntityTemplateProvider';
import { PreviewContent } from '../components/PreviewContent';
import { IDocumentPreviewContainerProps } from '../document-preview';
import { DocumentPreviewContent } from '../document-preview/components/DocumentPreviewContent';
import { DocumentPreviewTitle } from '../document-preview/components/DocumentPreviewTitle';
import { useDocumentPreview } from '../document-preview/hooks/use-document-preview';
import { EntityHistory } from '../EntityHistory';

import { DocumentCopyBanderole } from './components/DocumentCopyBanderole';
import { ErpPreviewTab } from './components/ErpPreviewTab';
import { SimpleReportWithFullScren } from './components/SimpleReportWithFullScreen';
import { useErpPreviewData } from './hooks/use-erp-preview-data';
import { useErpPreviewFields } from './hooks/use-erp-preview-fields';
import { useInboundInvoicePdfEditRights } from './hooks/use-inbound-invoice-pdf-edit-rights';
import { useMutateBzObject } from './hooks/use-mutate-bz-object';
import { usePdfOverlay } from './hooks/use-pdf-overlay';
import { usePreviewNavigation } from './hooks/use-preview-navigation';
import { useSimpleReportActions } from './hooks/use-simple-report-actions';

export interface IErpPreviewContainerProps
  extends Omit<
    IDocumentPreviewContainerProps,
    'documentId' | 'onEditPdfClicked'
  > {
  ids: number[];
  entity: Entities;
  openInFullscreen?: boolean;
  customActions?: CustomToolbar;
  convertProps?: Pick<IConvertPopoverProps, 'exclude' | 'onClick'>;
  languageResources: LanguageResource[];
  onVisibilityToggle?: (visible: boolean) => void;
}

export function ErpPreviewContainer(props: IErpPreviewContainerProps) {
  const {
    ids,
    entity,
    onCloseClick,
    onEditClicked,
    openInFullscreen = false,
    convertProps,
    languageResources,
    onVisibilityToggle,
  } = props;

  const { untypedPermissions } = usePermissions();

  const {
    data: entitiesData,
    refetch,
    fileList,
    requestEntityData,
  } = useErpPreviewData({ ids, entity });

  const { file, component } = usePreviewNavigation(fileList || []);

  const fileInfos = file?.fileInfos;
  const mimeType = file?.mimeType;

  const isSmUp = useMediaQuery<Theme>((t) => t.breakpoints.up('sm'));

  const re =
    entity === Entities.reViewModel
      ? (entitiesData?.[0] as ReViewModel)
      : undefined;

  const inboundInvoicePdfEditAllowed = useInboundInvoicePdfEditRights({
    fileName: file?.fileInfos?.fileEntityFilename,
    documentUserId: entitiesData?.[0]?.userId,
    datevDate: re?.invoiceDate,
  });

  const docId = useMemo(() => {
    const selectedEntity = re;

    const correctReceipt = selectedEntity?.receipts?.find(
      (x) => x.parentId === file?.id
    );

    return correctReceipt?.id || selectedEntity?.receipts?.[0]?.id;
  }, [file, re]);

  const [showPreview, setShowPreview] = useState(true);

  const reloadPreview = useCallback(async () => {
    await refetch();
    setShowPreview(false);
    setTimeout(() => setShowPreview(true));
  }, [refetch]);

  const { component: pdfOverlay, open: openPdfOverlay } = usePdfOverlay({
    entityId: re?.id,
    entityType: Entities.reViewModel,
    docId,
    onClose: reloadPreview,
  });

  const selectedRowsIdsList = useMemo(() => {
    return entitiesData?.map((x) => x.id) ?? [];
  }, [entitiesData]);

  const showEdit =
    isSmUp &&
    entitiesData?.length === 1 &&
    untypedPermissions(entity).canEdit(entitiesData?.[0]);

  const subtitleComp = useMemo(() => {
    if (entity === Entities.reViewModel && file?.parentId) {
      return <DocumentCopyBanderole />;
    }

    return null;
  }, [entity, file?.parentId]);

  const fields = useErpPreviewFields({
    entity,
    entitiesData,
  });

  const [tab, setTab] = useState<'ORVERVIEW' | 'HISTORY'>('ORVERVIEW');

  const documentPreviewProps = useDocumentPreview(
    fileInfos?.fileEntityFilename
  );

  const sharedProps = {
    onCloseClick,
    onEditClicked: showEdit ? onEditClicked : undefined,
    url: fileInfos?.previewUrl,
    filePath: fileInfos?.fileEntityFilename,
    mimeType: (fileInfos?.previewMimeType || mimeType) as MIME_TYPES,
    title:
      entitiesData?.[0]?.note || fileInfos?.fileServiceProviderInfos?.fileName,
    noPreviewUrl:
      entity !== Entities.reViewModel
        ? fileInfos?.fileServiceProviderInfos?.fspUrl || fileInfos?.downloadUrl
        : fileInfos?.downloadUrl,
    downloadUrl: fileInfos?.downloadUrl,
    fspUrl: fileInfos?.fileServiceProviderInfos?.fspUrl,
    exists: Boolean(fileInfos?.downloadUrl),
  };

  const entityTemplate = useEntityTemplate();

  const url = documentPreviewProps.isLinkable
    ? sharedProps.filePath
    : sharedProps.url;

  const mutate = useMutateBzObject();

  const noBzEntities = [Entities.reViewModel, Entities.raViewModel];
  const bzObjType = noBzEntities.includes(entity)
    ? null
    : convertEntityToBzObjType(entity);
  const mutateAll = useCallback(
    async (change: object) => {
      const ids = (entitiesData || []).map((x) => x.id);
      await mutate({
        variables: {
          ...change,
          ids,
          bzObjType,
        },
        onCompleted: () => {
          refetch();
        },
      });
    },
    [mutate, entitiesData, bzObjType, refetch]
  );

  const { actions, download, setSimpleReportBlob, fileName } =
    useSimpleReportActions({
      customActions: props.customActions,
      entitiesData,
      entity,
      file,
      setFullscreen: documentPreviewProps.setFullscreen,
      url,
      languageResources,
    });

  return (
    <React.Fragment>
      {pdfOverlay}
      {showPreview && (
        <div className={styles.wrapper}>
          <DocumentPreviewTitle
            {...documentPreviewProps}
            {...sharedProps}
            entries={entitiesData}
            entity={entity}
            entityData={requestEntityData}
            titleKeyField="note"
            openInFullscreen={openInFullscreen}
            navigation={component}
            erpPreview={true}
            showFullscreenButton={isSmUp}
            onPdfEditClicked={
              entity === Entities.reViewModel && inboundInvoicePdfEditAllowed
                ? () => openPdfOverlay()
                : undefined
            }
            customActions={actions}
            convertProps={convertProps}
            selectedRowsIdsList={selectedRowsIdsList}
            mutate={mutateAll}
            onVisibilityToggle={onVisibilityToggle}
          />
          {subtitleComp}

          {entity !== Entities.reViewModel &&
            entity !== Entities.raViewModel && (
              <>
                {entitiesData?.length === 1 && (
                  <ErpPreviewTab tab={tab} setTab={setTab} />
                )}
                {tab === 'ORVERVIEW' && (
                  <PreviewContent
                    style={{
                      borderBottom: '1px solid var(--ui04)',
                      background: 'var(--ui01)',
                      flex: 'unset',
                    }}
                    entries={entitiesData || []}
                    entity={entity}
                    fields={fields}
                    mutate={mutateAll}
                  />
                )}

                {tab === 'HISTORY' && entitiesData?.[0] && (
                  <EntityHistory
                    fitContainer={false}
                    entity={entity}
                    fullWidth
                    id={entitiesData[0].id}
                    className={styles.hist}
                  />
                )}
              </>
            )}
          {entity === Entities.reViewModel ||
          entity === Entities.raViewModel ? (
            <DocumentPreviewContent
              {...documentPreviewProps}
              {...sharedProps}
            />
          ) : (
            entitiesData?.length === 1 &&
            tab === 'ORVERVIEW' &&
            (url ? (
              <DocumentPreviewContent
                {...documentPreviewProps}
                {...sharedProps}
              />
            ) : (
              entitiesData?.[0] && (
                <SimpleReportWithFullScren
                  onClose={() => documentPreviewProps.setFullscreen(false)}
                  onDownloadClick={download}
                  onEditClicked={sharedProps.onEditClicked}
                  open={documentPreviewProps.detailInFullsceen}
                  fileName={fileName}
                  preview={entityTemplate.renderSimpleReport({
                    data: entitiesData[0],
                    bzObjType,
                    onPdfBlobChange: setSimpleReportBlob,
                  })}
                />
              )
            ))
          )}
        </div>
      )}
    </React.Fragment>
  );
}
